import { getCustomerContacts } from "@/apiv2/customers";
import * as customersActions from "@/redux/customers/customers.actions";
import { createAction, createSetterActions } from "@/redux/helpers/action";
import { handleError } from "@/redux/utils/error";

import { MODULE } from "./constants";

export const [
  setIsEmailEnabled,
  setEmailMessage,
  setSearchQuery,
  setContacts,
  setSelectedContactUids,
  setIsContactModalOpen,
  setIsFetchingContacts,
  setEditingContactUid,
] = createSetterActions(MODULE, [
  "isEmailEnabled",
  "emailMessage",
  "searchQuery",
  "contacts",
  "selectedContactUids",
  "isContactModalOpen",
  "isFetchingContacts",
  "editingContactUid",
]);

export const reset = createAction(`${MODULE}/RESET`);

// Persist a contact to the customer-contacts API. Wraps the customers thunk so
// the component never cross-imports another module's actions directly.
export const createContact =
  ({ customerUid, data }) =>
  (dispatch) => {
    if (!customerUid) {
      return undefined;
    }
    return dispatch(customersActions.createCustomerContacts(customerUid, data));
  };

// Update a server-side contact. Same wrapper rationale as createContact — keeps
// the component blind to the customers module. Edit is only fired for contacts
// originally persisted server-side (saveForFutureUse === true at the call site).
export const updateContact =
  ({ uid, customerUid, data }) =>
  (dispatch) => {
    if (!customerUid || !uid) {
      return undefined;
    }
    return dispatch(customersActions.updateCustomerContacts({ uid, customerUid, data }));
  };

// Server returns contacts shaped { uid, contactName, email, phone, mobile, ... };
// this card stores them as { uid, name, email, phone, mobile, saveForFutureUse }
// so AddContactModal and ContactRow can use one shape regardless of origin.
const mapServerContact = (raw) => ({
  uid: raw.uid,
  name: raw.contactName ?? raw.name ?? "",
  email: raw.email ?? "",
  phone: raw.phone ?? "",
  mobile: raw.mobile ?? "",
  // Contacts that come back from the API were already persisted server-side.
  saveForFutureUse: true,
});

export const fetchContacts =
  ({ customerUid }) =>
  async (dispatch) => {
    if (!customerUid) {
      return;
    }
    dispatch(setIsFetchingContacts(true));
    try {
      const response = await getCustomerContacts({ customerUid })();
      const rawContacts = response?.data ?? [];
      dispatch(setContacts(rawContacts.map(mapServerContact)));
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(setIsFetchingContacts(false));
    }
  };
